import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { updateUser, type ManagedUser } from "api/users";
import {
  Button,
  notify,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
} from "Components/ui";
import { useAuth } from "hooks/useAuth";
import UserFields, {
  editUserSchema,
  EMPTY_USER_FORM,
  type UserFormValues,
} from "./Components/UserFields";
import { displayName, getRoleId, getUserId, useRoleOptions } from "./data";

interface EditUserModalProps {
  open: boolean;
  /** A snapshot taken when the panel opens, so a background refetch can't reset the form. */
  user: ManagedUser | null;
  onClose: () => void;
  onSaved: () => void;
}

const toFormValues = (user: ManagedUser): UserFormValues => ({
  name: user.name || "",
  email: user.email || "",
  phoneNumber: user.phoneNumber || "",
  userName: user.userName || "",
  role: getRoleId(user),
  password: "",
  isActive: user.isActive,
});

/** Side panel that edits a user's profile, role and status (PUT /api/rbac/users/:id). */
const EditUserModal: React.FC<EditUserModalProps> = ({ open, user, onClose, onSaved }) => {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("userManagement", "write");
  const isOpen = open && Boolean(user) && canWrite;
  const roles = useRoleOptions(isOpen);

  const form = useForm<UserFormValues>({
    resolver: zodResolver(editUserSchema),
    defaultValues: EMPTY_USER_FORM,
  });
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = form;

  React.useEffect(() => {
    if (open && user) {
      reset(toFormValues(user));
    }
  }, [open, user, reset]);

  const onSubmit = async (values: UserFormValues) => {
    if (!canWrite) {
      notify.error("You don't have permission to edit users.");
      return;
    }
    const userId = user ? getUserId(user) : "";
    if (!userId) {
      notify.error("This user has no id, so they can't be edited.");
      return;
    }
    try {
      await updateUser(userId, {
        name: values.name,
        userName: values.userName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        role: values.role,
        isActive: values.isActive,
      });
      notify.success(`${values.name} saved`);
      onSaved();
    } catch (err) {
      notify.error(err, { fallback: "The changes weren't saved." });
    }
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) {
          onClose();
        }
      }}
    >
      <SheetContent>
        <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <SheetHeader
            title={user ? `Edit ${displayName(user)}` : "Edit user"}
            description="Profile, role and whether they can sign in."
          />
          <SheetBody>
            <UserFields form={form} mode="edit" roles={roles} />
          </SheetBody>
          <SheetFooter>
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Save changes
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default EditUserModal;
